import { useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  resetOrder,
  setPhysicalAddressRecipient,
  removePhysicalAddressRecipient,
  setCartItemRecipient,
  removeCartItemRecipient,
  createCashOrder,
  setStepId,
} from '../../../../../actions/cashActions';
import { resetToastAlert } from 'actions/toastAlertActions';
import { getCreateOrderRequest } from '../../../helpers/cash-order-helper';
import { steps } from '../ampol-cash-order-modal';

/**
 * Custom hook for managing all action handlers in Ampol Cash Order Modal
 *
 * Consolidates event handlers for:
 * - Recipient management (add, edit, remove, submit)
 * - Modal lifecycle (close, confirm cancel)
 * - Navigation (back to cart)
 * - Order placement
 *
 * @param params - Object containing Redux state and handler configurations
 * @returns Action handlers
 */
export const useAmpolCashOrderActions = ({
  // Redux state
  assignedGrandfatherClientNumber,
  cashOrderCartItems,
  cashOrderDefaults,
  deliveryOption,
  singleShippingAddress,
  physicalAddressRecipient,
  shippingMethod,
  isFeeWaiveOverride,
  promoDiscount,
  requestedName,
  requestedPhone,
  caseNumber,
  purchaseOrderNumber,
  // Selection state
  cartItemUuid,
  setCartItemUuid,
  setRecipientUuid,
  setFormId,
  // Form IDs
  deliveryOptionsFormId,
  addEditRecipientFormId,
  // GTM tracking
  trackCloseModal,
  trackPlaceOrder,
  // External callbacks
  onClose,
  cancelConfirmation,
}) => {
  const dispatch = useDispatch();

  // Store unstable callbacks in refs (prevents re-render loops)
  const onCloseRef = useRef(onClose);
  const trackCloseModalRef = useRef(trackCloseModal);
  const trackPlaceOrderRef = useRef(trackPlaceOrder);

  // Update refs every render (doesn't trigger re-renders)
  useEffect(() => {
    onCloseRef.current = onClose;
    trackCloseModalRef.current = trackCloseModal;
    trackPlaceOrderRef.current = trackPlaceOrder;
  });

  /**
   * Handle recipient form submission
   */
  const onRecipientSubmit = useCallback(
    (values) => {
      const { recipient, isPhysical, hasRemainingRecipients } = values;

      if (isPhysical) {
        dispatch(setPhysicalAddressRecipient(recipient));
      } else {
        dispatch(setCartItemRecipient(cartItemUuid, recipient));

        if (hasRemainingRecipients) {
          setRecipientUuid(null);
          return;
        }
      }

      // Navigate back to DeliveryOptions step after submission
      setFormId(deliveryOptionsFormId);
      dispatch(setStepId(steps.DeliveryOptions));
    },
    [dispatch, cartItemUuid, setRecipientUuid, setFormId, deliveryOptionsFormId]
  );

  /**
   * Handle add recipient button click
   */
  const onRecipientsAddClick = useCallback(
    (uuid) => {
      setCartItemUuid(uuid);
      setRecipientUuid(null);
      setFormId(addEditRecipientFormId);
      dispatch(setStepId(steps.AddEditRecipient));
    },
    [dispatch, setCartItemUuid, setRecipientUuid, setFormId, addEditRecipientFormId]
  );

  /**
   * Handle edit recipient button click
   */
  const onRecipientsEditClick = useCallback(
    (uuid, recipient) => {
      setCartItemUuid(uuid);
      setRecipientUuid(recipient.uuid);
      setFormId(addEditRecipientFormId);
      dispatch(setStepId(steps.AddEditRecipient));
    },
    [dispatch, setCartItemUuid, setRecipientUuid, setFormId, addEditRecipientFormId]
  );

  /**
   * Handle remove recipient button click
   */
  const onRecipientsRemoveClick = useCallback(
    (uuid, recipient, isPhysical) => {
      if (isPhysical) {
        dispatch(removePhysicalAddressRecipient());
      } else {
        dispatch(removeCartItemRecipient(uuid, recipient));
      }
    },
    [dispatch]
  );

  /**
   * Close modal and reset state
   */
  const closeModal = useCallback(() => {
    dispatch(resetOrder());
    dispatch(resetToastAlert());
    dispatch(setStepId(steps.Start));

    // Use ref to access latest callback
    if (trackCloseModalRef.current) {
      trackCloseModalRef.current();
    }

    if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, [dispatch]);

  /**
   * Confirm close with cancellation modal
   */
  const confirmClose = useCallback(() => {
    dispatch(resetToastAlert());
    if (cancelConfirmation?.open) {
      cancelConfirmation.open();
    }
  }, [dispatch, cancelConfirmation]);

  /**
   * Navigate back to Add/Edit Cart step
   */
  const onAddEditAmpolCashClick = useCallback(() => {
    dispatch(resetToastAlert());
    dispatch(setStepId(steps.Start));
  }, [dispatch]);

  /**
   * Place the cash order
   */
  const onPlaceOrder = useCallback(() => {
    const request = getCreateOrderRequest(
      cashOrderCartItems,
      cashOrderDefaults,
      deliveryOption,
      singleShippingAddress,
      physicalAddressRecipient,
      shippingMethod,
      isFeeWaiveOverride,
      promoDiscount,
      requestedName,
      requestedPhone,
      caseNumber,
      purchaseOrderNumber
    );

    dispatch(createCashOrder(assignedGrandfatherClientNumber, assignedGrandfatherClientNumber, request));

    // Use ref to access latest callback
    if (trackPlaceOrderRef.current) {
      trackPlaceOrderRef.current();
    }
  }, [
    dispatch,
    assignedGrandfatherClientNumber,
    cashOrderCartItems,
    cashOrderDefaults,
    deliveryOption,
    singleShippingAddress,
    physicalAddressRecipient,
    shippingMethod,
    isFeeWaiveOverride,
    promoDiscount,
    requestedName,
    requestedPhone,
    caseNumber,
    purchaseOrderNumber,
  ]);

  return {
    onRecipientSubmit,
    onRecipientsAddClick,
    onRecipientsEditClick,
    onRecipientsRemoveClick,
    closeModal,
    confirmClose,
    onAddEditAmpolCashClick,
    onPlaceOrder,
  };
};
